import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaBan, FaClock, FaExclamationTriangle, FaEye, FaSitemap, FaTasks, FaUserClock, FaUsers } from "react-icons/fa";
import { Stat } from "../dm/components/common";
import ItemDrawer from "./ItemDrawer";
import { Avatar, Empty, ItemCard, PageHead } from "./WorkUi";
import { errorMessage, fmtShortDate, toISODate } from "./workApi";
import "../dm/dm.css";
import "./work.css";

/** Manager view: the reporting tree's workload, late work, reviews and missing daily updates. */
function TeamBoard({ api }) {
  const [date, setDate] = useState(toISODate());
  const [dept, setDept] = useState("");
  const [area, setArea] = useState("");
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [openItem, setOpenItem] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(
        await api.teamWork({
          date,
          department_id: dept || undefined,
          area_id: area || undefined,
          user_id: selected || undefined,
        })
      );
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Could not load your team"));
    }
  }, [api, date, dept, area, selected]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.meta().then(setMeta).catch(() => null);
  }, [api]);

  const person = data?.people.find((p) => p.User_Id === selected);

  return (
    <div className="dmp wk">
      <PageHead title="Team Board">
        <Link className="dmp-btn dmp-btn--ghost" to={api.link("work/org")}>
          <FaSitemap aria-hidden="true" /> Reporting hierarchy
        </Link>
      </PageHead>

      <div className="wk-cal-filters">
        <label className="wk-sr-only" htmlFor="tb-date">
          Date
        </label>
        <input
          id="tb-date"
          type="date"
          value={date}
          max={toISODate()}
          onChange={(e) => setDate(e.target.value || toISODate())}
          style={{ border: "1px solid #cbd5e1", borderRadius: 8, padding: "7px 10px", minHeight: 38 }}
        />
        <select value={dept} onChange={(e) => setDept(e.target.value)} aria-label="Department">
          <option value="">All departments</option>
          {(meta?.departments || []).map((d) => (
            <option key={d.Department_Id} value={d.Department_Id}>
              {d.Department_Name}
            </option>
          ))}
        </select>
        <select value={area} onChange={(e) => setArea(e.target.value)} aria-label="Main area">
          <option value="">All areas</option>
          {(meta?.areas || []).map((a) => (
            <option key={a.Area_Id} value={a.Area_Id}>
              {a.Area_Name}
            </option>
          ))}
        </select>
      </div>
      {error ? <div className="dmp-error">{error}</div> : null}

      {!data ? (
        <div className="dmp-card wk-muted">Loading…</div>
      ) : (
        <>
          <div className="dmp-stats" style={{ marginBottom: 16 }}>
            <Stat label="People" value={data.totals.people} icon={FaUsers} tone="blue" />
            <Stat label="Open items" value={data.totals.open} icon={FaTasks} tone="indigo" />
            <Stat label="Late" value={data.totals.overdue} icon={FaExclamationTriangle} tone="rose" />
            <Stat label="Waiting review" value={data.totals.in_review} icon={FaEye} tone="violet" />
            <Stat label="Stuck" value={data.totals.blocked} icon={FaBan} tone="amber" />
            <Stat label="No update" value={data.totals.missing_updates} icon={FaUserClock} tone="amber" />
          </div>

          {data.missing.length ? (
            <div className="wk-banner" role="status">
              <span>
                <strong>No update on {fmtShortDate(data.date)}:</strong> {data.missing.map((p) => p.Name).join(", ")}
              </span>
              <span className="wk-muted" style={{ fontSize: 12 }}>
                They get an email reminder at 6 PM; you get a summary next morning.
              </span>
            </div>
          ) : null}

          {data.people.length ? (
            <div className="wk-people" role="group" aria-label="Team members">
              {data.people.map((p) => (
                <button
                  key={p.User_Id}
                  type="button"
                  className={`wk-person${selected === p.User_Id ? " is-selected" : ""}`}
                  onClick={() => setSelected(selected === p.User_Id ? null : p.User_Id)}
                  aria-pressed={selected === p.User_Id}
                >
                  <div className="wk-person__head">
                    <Avatar name={p.Name} size={36} />
                    <div>
                      <div className="wk-person__name">
                        {p.Name}
                        {p.Is_Me ? " (you)" : ""}
                      </div>
                      <div className="wk-person__sub">{[p.Designation, p.Department, p.Primary_Area].filter(Boolean).join(" · ") || "—"}</div>
                    </div>
                  </div>
                  <div className="wk-person__counts">
                    <span className="wk-count">{p.Counts.open} open</span>
                    {p.Counts.overdue ? <span className="wk-count wk-count--red">{p.Counts.overdue} late</span> : null}
                    {p.Counts.due_today ? <span className="wk-count">{p.Counts.due_today} today</span> : null}
                    {p.Counts.in_review ? <span className="wk-count wk-count--violet">{p.Counts.in_review} review</span> : null}
                    {p.Counts.blocked ? <span className="wk-count wk-count--amber">{p.Counts.blocked} stuck</span> : null}
                  </div>
                  <div className="wk-person__sub" style={{ marginTop: 8 }}>
                    <FaClock aria-hidden="true" />{" "}
                    {p.Updated_Today ? "Updated today" : p.Last_Update_On ? `Last update ${fmtShortDate(p.Last_Update_On)}` : "No updates yet"}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="dmp-card">
              <Empty icon={FaUsers} title="No one reports to you yet">
                Set managers on the <Link to={api.link("work/org")}>reporting hierarchy</Link> page.
              </Empty>
            </div>
          )}

          {selected ? (
            <div className="dmp-card" style={{ marginTop: 16 }}>
              <h3 className="dmp-card__title">{person ? `${person.Name}'s work` : "Work"}</h3>
              {data.items.length ? (
                data.items.map((item) => <ItemCard key={item.Item_Id} item={item} today={data.date} onOpen={(i) => setOpenItem(i.Item_Id)} />)
              ) : (
                <div className="wk-muted">No items.</div>
              )}
            </div>
          ) : null}
        </>
      )}
      {openItem ? <ItemDrawer api={api} itemId={openItem} meta={meta} onClose={() => setOpenItem(null)} onChanged={load} /> : null}
    </div>
  );
}

export default TeamBoard;
